import type {
  MediaAssetPublic,
  RoutePublic,
  Waypoint,
  WaypointsReplaceRequest,
} from '@xingroute/schema'
import {
  type EditableWaypoint,
  waypointToReplaceRequest,
  waypointsEqual,
} from './mergedWaypoints.js'

// Edit session utilities — types, change detection, and batch commit logic.
// Used by RouteDetailPage to unify all route edits into one save flow.

export interface DraftMediaItem {
  file: File
  previewUrl: string
  id: string // client-generated UUID for keying
  waypointId?: string // set for waypoint-attached media
}

export interface DraftMediaChanges {
  added: DraftMediaItem[]
  removed: string[] // existing media IDs to delete on commit
}

export interface CommitParams {
  routeId: string
  token: string
  startEtag: string
  currentStatus: RoutePublic['status']
  metadataPatch: Record<string, unknown> | null
  gpxFile: File | null
  trackReversed: boolean
  waypoints: EditableWaypoint[] | null
  mediaChanges: DraftMediaChanges
  publishDirect: boolean
  api: CommitApi
}

export interface CommitApi {
  patchRoute: (
    id: string,
    patch: Record<string, unknown>,
    token: string,
    etag: string,
  ) => Promise<{ route: RoutePublic; etag: string | null }>
  requestPublish: (
    id: string,
    token: string,
    etag: string,
  ) => Promise<{ route: RoutePublic; etag: string | null }>
  approvePublish: (
    id: string,
    token: string,
    etag: string,
  ) => Promise<{ route: RoutePublic; etag: string | null }>
  uploadGeometry: (
    id: string,
    file: File,
    token: string,
    etag: string,
  ) => Promise<{ etag: string | null; route: RoutePublic }>
  putWaypoints: (
    id: string,
    wps: WaypointsReplaceRequest['waypoints'],
    token: string,
    etag: string,
  ) => Promise<{ etag: string; waypoints: Waypoint[] }>
  uploadMedia: (
    id: string,
    file: File,
    token: string,
    opts?: { waypointId?: string },
  ) => Promise<MediaAssetPublic>
  deleteMedia: (id: string, mediaId: string, token: string) => Promise<void>
}

export interface CommitResult {
  success: boolean
  etag: string
  route?: RoutePublic
  waypoints?: Waypoint[]
  mutated?: boolean
  failedStep?: 'metadata' | 'gpx' | 'waypoints' | 'media-upload' | 'media-delete'
  failedDetail?: string
  failedError?: unknown
}

export async function commitAllEdits(params: CommitParams): Promise<CommitResult> {
  const {
    routeId,
    token,
    metadataPatch,
    gpxFile,
    trackReversed,
    waypoints,
    mediaChanges,
    publishDirect,
    api,
  } = params
  let etag = params.startEtag
  let route: RoutePublic | undefined
  let mutated = false

  // Step 1: metadata PATCH
  if (metadataPatch) {
    const patch = metadataPatchWithoutStatus(metadataPatch)
    if (patch) {
      try {
        const result = await api.patchRoute(routeId, patch, token, etag)
        etag = result.etag ?? etag
        route = result.route
        mutated = true
      } catch (err) {
        return {
          success: false,
          etag,
          mutated,
          failedStep: 'metadata',
          failedDetail: errMsg(err),
          failedError: err,
        }
      }
    }
  }

  // Step 2: GPX replace / reverse
  if (gpxFile) {
    try {
      const result = await api.uploadGeometry(routeId, gpxFile, token, etag)
      etag = result.etag ?? etag
      route = result.route
      mutated = true
    } catch (err) {
      return {
        success: false,
        etag,
        route,
        mutated,
        failedStep: 'gpx',
        failedDetail: errMsg(err),
        failedError: err,
      }
    }
  }
  // Note: trackReversed is handled by uploading a client-reversed GPX as gpxFile.
  // The caller builds the reversed file before passing it here.

  // Step 3: waypoints PUT
  let committedWaypoints: Waypoint[] | undefined
  if (waypoints) {
    try {
      const result = await api.putWaypoints(routeId, stripWaypoints(waypoints), token, etag)
      etag = result.etag
      committedWaypoints = result.waypoints
      mutated = true
    } catch (err) {
      return {
        success: false,
        etag,
        route,
        failedStep: 'waypoints',
        mutated,
        failedDetail: errMsg(err),
        failedError: err,
      }
    }
  }

  // Step 4: media uploads (逐个，成功后从 added splice)
  const { added, removed } = mediaChanges
  for (let i = 0; i < added.length; ) {
    const item = added[i]!
    try {
      await api.uploadMedia(
        routeId,
        item.file,
        token,
        item.waypointId ? { waypointId: item.waypointId } : undefined,
      )
      if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(item.previewUrl)
      added.splice(i, 1) // remove from draft on success
      mutated = true
    } catch (err) {
      return {
        success: false,
        etag,
        route,
        waypoints: committedWaypoints,
        mutated,
        failedStep: 'media-upload',
        failedDetail: errMsg(err),
        failedError: err,
      }
    }
  }

  // Step 5: media deletes (逐个，成功后从 removed splice)
  for (let i = 0; i < removed.length; ) {
    const mediaId = removed[i]!
    try {
      await api.deleteMedia(routeId, mediaId, token)
      removed.splice(i, 1)
      mutated = true
    } catch (err) {
      return {
        success: false,
        etag,
        route,
        waypoints: committedWaypoints,
        mutated,
        failedStep: 'media-delete',
        failedDetail: errMsg(err),
        failedError: err,
      }
    }
  }

  const finalStatus = route?.status ?? params.currentStatus
  if (publishDirect && finalStatus !== 'published') {
    try {
      if (finalStatus === 'draft') {
        const requested = await api.requestPublish(routeId, token, etag)
        etag = requested.etag ?? etag
        route = requested.route
        mutated = true
      }
      if ((route?.status ?? params.currentStatus) === 'pending_review') {
        const approved = await api.approvePublish(routeId, token, etag)
        etag = approved.etag ?? etag
        route = approved.route
        mutated = true
      }
    } catch (err) {
      return {
        success: false,
        etag,
        route,
        mutated,
        failedStep: 'metadata',
        failedDetail: errMsg(err),
        failedError: err,
      }
    }
  }

  return { success: true, etag, route, waypoints: committedWaypoints, mutated }
}

// --- Change detection ---

export function hasMetadataChanges(draft: Record<string, unknown>, route: RoutePublic): boolean {
  const fields = ['title', 'summary', 'difficulty', 'route_type'] as const
  for (const f of fields) {
    const dv = draft[f] ?? null
    const rv = (route as Record<string, unknown>)[f] ?? null
    if (dv !== rv) return true
  }
  if (JSON.stringify(draft.activities ?? null) !== JSON.stringify(route.activities ?? null))
    return true
  if (JSON.stringify(draft.region ?? null) !== JSON.stringify(route.region ?? null)) return true
  if (
    JSON.stringify(draft.natural_features ?? null) !==
    JSON.stringify(route.natural_features ?? null)
  )
    return true
  if (JSON.stringify(draft.seasonality ?? null) !== JSON.stringify(route.seasonality ?? null))
    return true
  return false
}

export function hasWaypointChanges(
  draft: EditableWaypoint[] | null,
  server: readonly Waypoint[],
): boolean {
  if (!draft) return false
  return !waypointsEqual(server, draft)
}

export function hasGpxChanges(gpxFile: File | null, trackReversed: boolean): boolean {
  return gpxFile !== null || trackReversed
}

export function hasMediaChanges(changes: DraftMediaChanges): boolean {
  return changes.added.length > 0 || changes.removed.length > 0
}

export function hasAnyChanges(
  draft: Record<string, unknown>,
  route: RoutePublic,
  draftWaypoints: EditableWaypoint[] | null,
  serverWaypoints: readonly Waypoint[],
  gpxFile: File | null,
  trackReversed: boolean,
  mediaChanges: DraftMediaChanges,
): boolean {
  return (
    hasMetadataChanges(draft, route) ||
    hasWaypointChanges(draftWaypoints, serverWaypoints) ||
    hasGpxChanges(gpxFile, trackReversed) ||
    hasMediaChanges(mediaChanges)
  )
}

// --- Helpers ---

function errMsg(err: unknown): string {
  if (err instanceof Error) return err.message
  return String(err)
}

function metadataPatchWithoutStatus(
  patch: Record<string, unknown>,
): Record<string, unknown> | null {
  const { status: _status, ...metadata } = patch
  return Object.keys(metadata).length > 0 ? metadata : null
}

function stripWaypoints(wps: EditableWaypoint[]): WaypointsReplaceRequest['waypoints'] {
  return wps.map(waypointToReplaceRequest)
}
